import { Layout } from "@layouts";
import { Bar, useGiftCardOffersTable } from "@organisms";
import {
  giftCardOfferColumns,
  PageBreadcrumbs,
  Card,
} from "@molecules";
import { Spinner } from "@atoms";
import { useEffect, useState, useMemo } from "react";
import { GiftCardAmountSelectorItem, GiftCardOffer, PartnerAirdropTransaction } from "../../../../types";
import { useTranslation } from "react-i18next";
import {
  Button as SButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input as SInput,
} from "@shadcn";
import { PagePaths } from "../../../../types";
import IconParachuteCard from "../../../../assets/phone_airdrop.jpg";
import { X } from "lucide-react";
const HERO_SECTION_HEIGHT = 288;

export type AirdropPageTemplateProps = {
  premiumUsers: any[];
  giftCardOffers: GiftCardOffer[] | null;
  navigateToPage: (index: number, to: string) => void;
  onReturnPressed: () => void;
  partnerAirdropTransactions: PartnerAirdropTransaction[];
  isHistoryLoading: boolean;
  historyError: any;
  creditCardStatus?: "valid" | "expired" | "expiringSoon";
};

type SummaryCard = {
  id: string;
  title: string;
  value: string;
};

export const AirdropPageTemplate = ({
  premiumUsers,
  giftCardOffers,
  navigateToPage,
  onReturnPressed,
  partnerAirdropTransactions,
  isHistoryLoading,
  historyError,
  creditCardStatus = "valid",
}: AirdropPageTemplateProps): JSX.Element => {
  // const { partner } = useContext(AuthContext);
  const { t } = useTranslation();
  const [giftCardOfferAmounts, setGiftCardOfferAmounts] =
    useState<Array<GiftCardAmountSelectorItem> | null>(null);
  const [historySearchTerm, setHistorySearchTerm] = useState("");
  const { rowSelection } = useGiftCardOffersTable({
    columns: giftCardOfferColumns,
    data: giftCardOffers,
    searchable: true,
  });

  useEffect(() => {
    const keys = Object.keys(rowSelection);
    if (keys.length > 0) {
      const presetAmounts = giftCardOffers?.[Number(keys)].presetAmounts.filter(
        (item) => item !== 0,
      );
      const giftCardOfferAmounts: GiftCardAmountSelectorItem[] | undefined =
        presetAmounts?.map((item): GiftCardAmountSelectorItem => {
          return {
            value: item.toString(),
            text: `${item.toString()}`,
          };
        });
      giftCardOfferAmounts === undefined
        ? setGiftCardOfferAmounts(null)
        : setGiftCardOfferAmounts(giftCardOfferAmounts);
    } else if (keys.length === 0) {
      setGiftCardOfferAmounts(null);
    }
  }, [rowSelection]);

  useEffect(() => {
    setGiftCardOfferAmounts(null);
  }, []);

  const goToPerks = () => navigateToPage(3, `/dashboard/${PagePaths.perks}`);

  const historyRows = partnerAirdropTransactions
    ? partnerAirdropTransactions.map((transaction, index) => {
        const premiumUser = premiumUsers.find(
          (user) => user.userId === transaction.userId,
        );
        const firstName = premiumUser?.firstName ?? "";
        const lastName = premiumUser?.lastName ?? "";
        const fullName = `${firstName} ${lastName}`.trim() || "--";
        const brandName =
          transaction.externalRef?.data?.externalData?.brandName ?? "--";
        const totalSaleAmount = transaction.externalRef?.data?.totalSaleAmount;
        const receiptUrl =
          transaction.externalRef?.data?.billingInformation
            ?.partner_receipt_url ?? null;
        const airdropDate = transaction.completionDate ?? transaction.createdAt ?? null;
        return {
          key: `${transaction.userId ?? "user"}-${index}`,
          fullName,
          brandName,
          totalSaleAmount,
          receiptUrl,
          airdropDate,
        };
      })
    : [];

  const filteredHistoryRows = useMemo(() => {
    const term = historySearchTerm.trim().toLowerCase();
    if (!term) {
      return historyRows;
    }
    return historyRows.filter((row) => {
      const fullName = row.fullName?.toLowerCase() ?? "";
      const brandName = row.brandName?.toLowerCase() ?? "";
      return fullName.includes(term) || brandName.includes(term);
    });
  }, [historyRows, historySearchTerm]);

  const formatSaleAmount = (amount?: number | null): string => {
    if (typeof amount !== "number" || Number.isNaN(amount)) {
      return "--";
    }

    return `$${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const totalAirdropsSent = partnerAirdropTransactions?.length ?? 0;
  const totalValueSentRaw =
    partnerAirdropTransactions?.reduce((sum, transaction) => {
      const value = transaction.externalRef?.data?.totalSaleAmount;
      return typeof value === "number" ? sum + value : sum;
    }, 0) ?? 0;
  const summaryCards: SummaryCard[] = [
    {
      id: "totalAirdrops",
      title: t("airdrop.summaryCards.totalAirdropsSent"),
      value: totalAirdropsSent.toLocaleString(),
    },
    {
      id: "totalValue",
      title: t("airdrop.summaryCards.totalValueSent"),
      value: formatSaleAmount(totalValueSentRaw),
    },
  ];

  const formatAirdropDate = (date?: string | null): string => {
    if (!date) {
      return "--";
    }
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) {
      return "--";
    }
    return parsedDate.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Layout
      variant="dashboard"
      header={t("airdrop.common.header")}
      subheader={
        <div className="flex flex-row justify-between max-w-5xl">
          <div className="page-subtitle mt-2">
            {t("airdrop.common.subheader")}
          </div>
        </div>
      }
    >
      <div className="flex flex-col h-full">
        <PageBreadcrumbs
          items={[
            { label: t("airdrop.common.breadcrumbs.benefits"), onClick: goToPerks },
            { label: t("airdrop.common.breadcrumbs.airdrops") },
          ]}
        />
        <div className="flex flex-col pb-8">
          <div
            className="flex flex-col md:flex-row items-center md:items-start gap-6 max-w-5xl border border-neutral-light rounded-2xl p-6 bg-white mb-6"
            style={{ minHeight: HERO_SECTION_HEIGHT }}
          >
            <img
              src={IconParachuteCard}
              alt={t("airdrop.hero.alt")}
              className="w-28 md:w-40 lg:w-40 lg:min-w-40 lg:max-w-40 h-auto rounded-xl border border-neutral-medium"
            />
            <div className="flex flex-col text-left space-y-3 pl-4">
              <p className="text-sm font-semibold text-neutral-mediumDark uppercase tracking-wide">
                {t("airdrop.hero.kicker")}
              </p>
              <h3 className="text-neutral-darkest text-2xl font-semibold">
                {t("airdrop.hero.title")}
              </h3>
              <p className="text-neutral-dark text-base font-normal pb-2">
                {t("airdrop.hero.description")}
              </p>
              <SButton
                onClick={
                  creditCardStatus === "expired"
                    ? undefined
                    : () => navigateToPage(1, "/dashboard/airdrop/select-members")
                }
                disabled={creditCardStatus === "expired"}
                className={`self-start rounded-full text-white text-sm font-semibold px-6 py-3 transition-colors ${
                  creditCardStatus === "expired"
                    ? "bg-neutral-light cursor-not-allowed disabled:bg-neutral-light disabled:text-white disabled:opacity-100"
                    : "bg-brand-orange hover:bg-primary-dark"
                }`}
              >
                {t("airdrop.hero.cta")}
              </SButton>
            </div>
          </div>
          <div className="max-w-5xl">
            <div className="flex flex-col gap-6 border border-neutral-light bg-white rounded-2xl w-full min-w-[400px] p-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-4">
                <div className="flex w-full flex-col gap-3 mr-4 md:max-w-sm">
                  <h2 className="section-title">
                    {t("airdrop.history.title")}
                  </h2>
                  <div className="w-full relative">
                    <SInput
                      placeholder={t("airdrop.history.searchPlaceholder")}
                      value={historySearchTerm}
                      onChange={(event) => setHistorySearchTerm(event.target.value)}
                      className="w-full pr-10 input-focus-primary"
                    />
                    {historySearchTerm ? (
                      <button
                        type="button"
                        onClick={() => setHistorySearchTerm("")}
                        className="absolute inset-y-0 right-3 flex items-center text-neutral-medium hover:text-neutral-dark focus:outline-none"
                        aria-label="Clear search"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row w-full gap-4">
                  {summaryCards.map((card) => (
                    <Card
                      key={card.id}
                      variant="metric"
                      label={card.title}
                      value={card.value}
                      className="w-full sm:w-1/2 flex-1 min-h-[7.25rem] h-auto border border-neutral-light shadow-none rounded-2xl p-4"
                      valueClassName="text-2xl font-medium text-neutral-darkest"
                      labelClassName="metric-subtitle"
                    />
                  ))}
                </div>
              </div>
              {isHistoryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center">
                      <Spinner variant="large" />
                    </div>
                    <p className="text-gray-600">{t("airdrop.history.loading")}</p>
                  </div>
                </div>
              ) : historyError ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-red-500 text-2xl">⚠️</span>
                    </div>
                    <p className="text-gray-600 mb-2">{t("airdrop.history.errorTitle")}</p>
                    <p className="text-sm text-gray-500">{t("airdrop.history.errorSubtitle")}</p>
                  </div>
                </div>
              ) : historyRows.length ? (
                <div className="rounded-lg border border-neutral-light overflow-hidden">
                  <Table className="border-collapse">
                    <TableHeader className="bg-neutral-lightest">
                      <TableRow className="border-b border-neutral-light">
                        <TableHead className="pl-[10px] px-4 text-xs font-medium text-neutral-mediumDark uppercase tracking-wider">
                          {t("airdrop.history.tableHeaders.fullName")}
                        </TableHead>
                        <TableHead className="px-4 text-xs font-medium text-neutral-mediumDark uppercase tracking-wider">
                          {t("airdrop.history.tableHeaders.date")}
                        </TableHead>
                        <TableHead className="px-4 text-xs font-medium text-neutral-mediumDark uppercase tracking-wider">
                          {t("airdrop.history.tableHeaders.brand")}
                        </TableHead>
                        <TableHead className="px-4 text-xs font-medium text-neutral-mediumDark uppercase tracking-wider">
                          {t("airdrop.history.tableHeaders.totalSaleAmount")}
                        </TableHead>
                        <TableHead className="pr-[10px] pr-8 text-center text-xs font-medium text-neutral-mediumDark uppercase tracking-wider">
                          {t("airdrop.history.tableHeaders.receipt")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredHistoryRows.length ? (
                        filteredHistoryRows.map((row, index) => {
                          const isLastRow = index === filteredHistoryRows.length - 1;
                          const borderClass = isLastRow ? "" : "border-b border-neutral-light";

                          return (
                            <TableRow key={row.key} className="border-b-0 hover:bg-primary-lightest">
                              <TableCell
                                className={`pl-[10px] px-4 text-sm font-medium text-neutral-darkest ${borderClass}`}
                              >
                                {row.fullName}
                              </TableCell>
                              <TableCell
                                className={`px-4 text-xs text-neutral-dark ${borderClass}`}
                              >
                                {formatAirdropDate(row.airdropDate)}
                              </TableCell>
                              <TableCell
                                className={`px-4 text-xs text-neutral-dark ${borderClass}`}
                              >
                                {row.brandName}
                              </TableCell>
                              <TableCell
                                className={`px-4 text-xs text-neutral-dark ${borderClass}`}
                              >
                                {formatSaleAmount(row.totalSaleAmount)}
                              </TableCell>
                              <TableCell
                                className={`px-4 text-xs text-neutral-dark ${borderClass} text-center`}
                              >
                                {row.receiptUrl ? (
                                  <a
                                    className="text-primary-medium hover:text-primary-dark text-xs font-medium"
                                    href={row.receiptUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {t("airdrop.history.receipt.view")}
                                  </a>
                                ) : (
                                  <span className="text-xs text-neutral-mediumDark">{t("airdrop.history.receipt.none")}</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="py-6 text-center metric-subtitle"
                          >
                            {t("airdrop.history.emptySearch")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex items-center justify-start py-8">
                  <div className="text-center text-gray-500">
                    <p className="text-base">
                      {t("airdrop.history.emptyHistory")}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <Bar
          variant="bottom"
          onReturnPressed={onReturnPressed}
          onContinuePressed={null}
          isContinueDisabled={true}
        />
      </div>
    </Layout>
  );
};
